import { type TFunction } from 'i18next'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { LoaderFunctionArgs } from 'react-router-dom'
import {
  isRouteErrorResponse,
  json,
  useLoaderData,
  useNavigate,
  useParams,
  useRouteError
} from 'react-router-dom'
import styled from 'styled-components'

import {
  Column,
  Container,
  DelayedSpinner,
  EmptyState,
  PageHeader,
  Row,
  ScrollableWrapper
} from '@cdab/scania/qpr/components'
import { getClient } from '@cdab/scania/qpr/contexts/backend-provider'
import { useTitle } from '@cdab/scania/qpr/contexts/title'
import {
  useCreateSyncDealersForMarket,
  useCssVariableBreakpoint,
  useHandleGetSisImportJobStatus
} from '@cdab/scania/qpr/hooks'
import type { Dealer, Market, User } from '@cdab/scania/qpr/schema'
import { CertificationStatus, Roles } from '@cdab/scania/qpr/schema'
import {
  filterTableData,
  sortTableData,
  type SortConfig
} from '@cdab/scania/qpr/utils'
import { capitalizeFirstLetter, formatISODate, isNumber } from '@cdab/utils'
import {
  TdsBodyCell,
  TdsButton,
  TdsDropdown,
  TdsDropdownOption,
  TdsHeaderCell,
  TdsTable,
  TdsTableBody,
  TdsTableBodyRow,
  TdsTableHeader,
  TdsTableToolbar,
  TdsToast
} from '@scania/tegel-react'
import { toast } from 'react-toastify'
import { SpinnerWrapper } from './dealer/dealer.styles'

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`

const DropdownWrapper = styled.div`
  align-self: flex-end;
  margin-top: 16px;
  flex: 1;

  @media (min-width: ${({ theme }) => theme.size.lg}) {
    flex: initial;
  }
`

const SyncButtonWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  padding-top: 8px;
  flex-direction: column;
`

const StyledButton = styled(TdsButton)`
  margin-top: 16px;
  margin-bottom: 4px;
  @media (max-width: ${({ theme }) => theme.size.lg}) {
    margin-bottom: 8px;
    margin-top: 0;
  }
`

type Params = {
  marketId: string
}

type PreparedTableData = {
  city: string
  dealerId: string
  name: string
  auditDate: string
  expiryDate: string | null
  activeDeviations: string
  id: number
}

function prepareTableData(
  data: readonly Dealer[],
  t: TFunction
): PreparedTableData[] {
  const preparedData = data.map(dealer => {
    let expiryDateInfo: string | null = null

    switch (dealer.certificationStatus) {
      case CertificationStatus.Certified:
        expiryDateInfo = dealer.expiryDate
          ? formatISODate(dealer.expiryDate)
          : ''
        break

      case CertificationStatus.Expired:
        expiryDateInfo = t('certification-status.expired', { ns: 'common' })
        break

      case CertificationStatus.NeverCertified:
        expiryDateInfo = t('certification-status.never-certified', {
          ns: 'common'
        })
        break
    }

    return {
      city: dealer.city,
      dealerId: dealer.dealerId,
      name: dealer.name,
      auditDate: dealer.auditDate ? formatISODate(dealer.auditDate) : '',
      expiryDate: expiryDateInfo,
      activeDeviations: dealer.hasDeviations
        ? t('yes', { ns: 'common' })
        : t('no', { ns: 'common' }),
      id: dealer.id
    }
  })

  return preparedData
}

export type MarketPageLoaderData = {
  dealers: Dealer[]
  markets: Market[]
  currentUser: User
}

export async function loader({
  params
}: LoaderFunctionArgs): Promise<MarketPageLoaderData> {
  const { marketId: marketIdParam } = params
  const marketId = Number.parseInt(marketIdParam || 'NaN')

  if (!isNumber(marketId)) {
    throw json({
      status: 400
    })
  }

  const client = getClient()

  const user = await client.GetuserInfo()
  if (!user) {
    throw json({
      status: 401
    })
  }

  const marketsPromise = client.MarketsService.GetMarketsForUser(user.userId)
  const dealersPromise = client.DealersService.GetDealersForMarket(marketId)
  const currentUserPromise = client.UserService.GetUser(user.userId)

  const [markets, dealers, currentUser] = await Promise.all([
    marketsPromise,
    dealersPromise,
    currentUserPromise
  ])
  return {
    markets,
    dealers,
    currentUser
  }
}

export function MarketPage() {
  const { t } = useTranslation(['market', 'common', 'dealer'])
  const { dealers, markets, currentUser } =
    useLoaderData() as MarketPageLoaderData
  const navigate = useNavigate()
  const params = useParams<Params>()
  const { marketId } = params
  const { updateTitles, desktop } = useTitle()
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')

  const [sortConfig, setSortConfig] = useState<SortConfig>(null)
  const [filterQuery, setFilterQuery] = useState('')
  const [processedData, setProcessedData] = useState<PreparedTableData[]>([])

  const [handleClickCreateSISSyncState] = useCreateSyncDealersForMarket(
    Number(marketId)
  )

  const onDealerRowClick = (dealerId: string) => {
    if (dealerId) {
      navigate(`/dealer/${dealerId}`)
    }
  }

  const [
    stateGetSisImportJobStatus,
    handleCallGetSisImportJobStatus,
    setStateGetSisImportJobStatus
  ] = useHandleGetSisImportJobStatus()

  const canCurrentUserSyncDealersForMarket =
    currentUser.role >= Roles.CoOrdinator

  const market: Market | undefined = markets.find(
    market => market.id === Number(marketId)
  )

  const onSelectMarket = useCallback(
    (marketId: string | null) => {
      if (marketId === null) return
      navigate(`/dealers/${marketId}`)
    },
    [navigate]
  )

  const handleDealersSyncClick = async () => {
    const sisId = await handleClickCreateSISSyncState()
    if (sisId) handleCallGetSisImportJobStatus(sisId)
  }

  const handleTableSort = useCallback(
    (
      event: CustomEvent<{
        tableId: string | undefined
        columnKey: string | undefined
        sortingDirection: 'asc' | 'desc'
      }>
    ) => {
      if (!event.detail.columnKey) {
        setSortConfig(null)
        return
      }
      const { columnKey, sortingDirection } = event.detail
      setSortConfig({ columnKey, sortingDirection })
    },
    []
  )

  const handleTableFilter = useCallback(
    (
      event: CustomEvent<{
        tableId: string | undefined
        query: string | undefined
      }>
    ) => {
      if (!event.detail.query) {
        setFilterQuery('')
        return
      }
      const { query } = event.detail
      setFilterQuery(query)
    },
    []
  )

  useEffect(() => {
    let data = prepareTableData(dealers, t)

    if (filterQuery) {
      data = filterTableData(data, filterQuery)
    }

    if (sortConfig) {
      data = sortTableData(data, sortConfig)
    }

    setProcessedData(data)
  }, [dealers, sortConfig, filterQuery, t])

  useEffect(() => {
    if (stateGetSisImportJobStatus.status === 'success') {
      toast(
        <TdsToast
          variant='success'
          header={capitalizeFirstLetter(
            t('message.sync-dealers-succuss', { ns: 'market' })
          )}
          subheader={stateGetSisImportJobStatus.message}
        />
      )
      setStateGetSisImportJobStatus({ status: 'none', message: null })
    }

    if (stateGetSisImportJobStatus.status === 'error') {
      toast(
        <TdsToast
          variant={stateGetSisImportJobStatus.status}
          header={capitalizeFirstLetter(
            t('message.sync-dealers-error', { ns: 'market' })
          )}
          subheader={stateGetSisImportJobStatus.message}
        />,
        {
          autoClose: false
        }
      )
      setStateGetSisImportJobStatus({ status: 'none', message: null })
    }
  }, [
    navigate,
    t,
    stateGetSisImportJobStatus.status,
    stateGetSisImportJobStatus.message,
    setStateGetSisImportJobStatus
  ])

  useEffect(() => {
    updateTitles({
      desktop: {
        title: desktop.title
      },
      mobile: {
        title: market?.marketName || t('market'),
        description: null
      }
    })
  }, [desktop.title, t, updateTitles, market?.marketName])

  if (!market) {
    return (
      <Container
        fluid='normal'
        paddingOnColumns={!isLg}
        paddingOnContainer={!isLg}
      >
        <Row>
          <Column width={12} padding={isLg}>
            <EmptyState title={t('market-not-found', { ns: 'market' })} />
          </Column>
        </Row>
      </Container>
    )
  }

  return (
    <ScrollableWrapper>
      <Container fluid='normal'>
        {isLg && (
          <Row>
            <Column width={12} padding={isLg}>
              <PageHeader title={market.marketName} />
            </Column>
          </Row>
        )}
        <Row>
          {markets.length > 0 && (
            <Column
              width={12}
              lg={4}
              padding={isLg}
              style={{ overflow: 'inherit' }}
            >
              <HeaderRow>
                <DropdownWrapper>
                  <TdsDropdown
                    modeVariant='secondary'
                    onTdsChange={e => onSelectMarket(e.detail.value)}
                    filter={true}
                    defaultValue={marketId}
                    placeholder={t('available-markets')}
                  >
                    {markets
                      .sort((a, b) => a.marketName.localeCompare(b.marketName))
                      .map(market => (
                        <TdsDropdownOption
                          key={market.id}
                          value={market.id.toString()}
                        >
                          {market.marketName}
                        </TdsDropdownOption>
                      ))}
                  </TdsDropdown>
                </DropdownWrapper>
              </HeaderRow>
            </Column>
          )}
          <Column width={12} lg={markets.length > 0 ? 8 : 12}>
            <SyncButtonWrapper>
              {canCurrentUserSyncDealersForMarket &&
                (stateGetSisImportJobStatus.status === 'syncing' ? (
                  <SpinnerWrapper>
                    <DelayedSpinner size='md' />
                  </SpinnerWrapper>
                ) : (
                  <StyledButton
                    text={capitalizeFirstLetter(
                      t('sync-dealers', {
                        ns: 'market'
                      })
                    )}
                    variant='primary'
                    size='md'
                    onClick={handleDealersSyncClick}
                  />
                ))}
            </SyncButtonWrapper>
          </Column>
        </Row>

        <Row>
          <Column width={12} padding={isLg}>
            <ScrollableWrapper>
              <TdsTable
                compactDesign={false}
                noMinWidth={!isLg}
                verticalDividers={false}
                responsive={true}
                modeVariant='secondary'
              >
                <TdsTableToolbar
                  tableTitle={t('dealers-in', { market: market.marketName })}
                  filter
                  onTdsFilter={handleTableFilter}
                />
                <TdsTableHeader>
                  <TdsHeaderCell
                    onTdsSort={handleTableSort}
                    cellKey='city'
                    cellValue={t('city', { ns: 'common' })}
                    sortable
                  />
                  <TdsHeaderCell
                    onTdsSort={handleTableSort}
                    cellKey='dealerId'
                    cellValue={t('dealer-id')}
                    sortable
                  />
                  <TdsHeaderCell
                    onTdsSort={handleTableSort}
                    cellKey='name'
                    cellValue={t('name', { ns: 'common' })}
                    sortable
                  />
                  <TdsHeaderCell
                    onTdsSort={handleTableSort}
                    cellKey='auditDate'
                    cellValue={t('audit-date', { ns: 'common' })}
                    sortable
                  />
                  <TdsHeaderCell
                    onTdsSort={handleTableSort}
                    cellKey='expiryDate'
                    cellValue={t('expiry-date', { ns: 'common' })}
                    sortable
                  />
                  <TdsHeaderCell
                    onTdsSort={handleTableSort}
                    cellKey='activeDeviations'
                    cellValue={t('active-deviations')}
                    sortable
                  />
                  <TdsHeaderCell
                    onTdsSort={handleTableSort}
                    cellKey='id'
                    cellValue='ID'
                    sortable
                  />
                </TdsTableHeader>
                <TdsTableBody>
                  {processedData.map(row => (
                    <TdsTableBodyRow
                      key={row.id}
                      onClick={() => onDealerRowClick(row.id.toString())}
                    >
                      <TdsBodyCell cellKey='city'>{row.city}</TdsBodyCell>
                      <TdsBodyCell cellKey='dealerId'>
                        {row.dealerId}
                      </TdsBodyCell>
                      <TdsBodyCell cellKey='name'>{row.name}</TdsBodyCell>
                      <TdsBodyCell cellKey='auditDate'>
                        {row.auditDate}
                      </TdsBodyCell>
                      <TdsBodyCell cellKey='expiryDate'>
                        {row.expiryDate}
                      </TdsBodyCell>
                      <TdsBodyCell cellKey='activeDeviations'>
                        {row.activeDeviations}
                      </TdsBodyCell>
                      <TdsBodyCell cellKey='id'>{row.id}</TdsBodyCell>
                    </TdsTableBodyRow>
                  ))}
                </TdsTableBody>
              </TdsTable>
            </ScrollableWrapper>
          </Column>
        </Row>
      </Container>
    </ScrollableWrapper>
  )
}

export function ErrorBoundary() {
  const { t } = useTranslation(['errors', 'common'])
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const navigate = useNavigate()
  const error = useRouteError()

  function goToMarkets() {
    navigate('/dealers')
  }

  if (isRouteErrorResponse(error)) {
    let message = t('talk-to-admin')

    switch (error.status) {
      case 401:
        message = t('not-logged-in')
        break
      case 400:
        message = t('invalid-market-id')
        break
    }

    return (
      <Container
        fluid='normal'
        paddingOnColumns={!isLg}
        paddingOnContainer={!isLg}
      >
        <Row>
          <Column width={12} padding={isLg}>
            <EmptyState title={t('market-not-found')} description={message}>
              <TdsButton onClick={goToMarkets} text={t('go-to-dealers')} />
            </EmptyState>
          </Column>
        </Row>
      </Container>
    )
  }

  throw error
}
